"use client";

import Image from "next/image";
import { Plus, Check, Camera } from "lucide-react";
import { useVehicleUploads } from "@/hooks/use-vehicle-uploads";
import { cn } from "@/lib/utils";
import { useRef, useEffect, useCallback } from "react";

interface RecentVehiclesProps {
  selectedUploadId: string | null;
  onSelect: (uploadId: string, imageUrl: string) => void;
  onFileSelect?: (file: File) => void;
  disabled?: boolean;
  className?: string;
}

export function RecentVehicles({
  selectedUploadId,
  onSelect,
  onFileSelect,
  disabled = false,
  className,
}: RecentVehiclesProps) {
  const { data, isLoading } = useVehicleUploads();
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onFileSelect) {
      onFileSelect(file);
    }
    // Reset input so the same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  }, [onFileSelect]);

  const uploads = data?.uploads || [];
  const hasUploads = uploads.length > 0;

  // Auto-scroll to selected car
  useEffect(() => {
    if (selectedUploadId && scrollRef.current) {
      const selectedElement = scrollRef.current.querySelector(
        `[data-upload-id="${selectedUploadId}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [selectedUploadId]);

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  if (isLoading) {
    return (
      <div className={cn("", className)}>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="shrink-0 w-28 h-20 bg-white/5 animate-pulse rounded-xl"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("", className)}>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-zinc-400">
          {hasUploads ? "Your Cars" : "Get Started"}
        </h3>
        {hasUploads && (
          <span className="text-xs text-zinc-500">{uploads.length} saved</span>
        )}
      </div>

      {/* Horizontal scroll container - p-1 prevents ring clipping */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto py-1 px-1 -mx-1 scrollbar-hide snap-x snap-mandatory"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {/* Upload New button - always first */}
        <label
          className={cn(
            "shrink-0 w-28 h-20 rounded-xl border-2 border-dashed transition-all duration-300 snap-start",
            "flex flex-col items-center justify-center gap-1.5",
            "bg-white/2 border-white/15",
            !disabled && "cursor-pointer hover:border-blue-500/50 hover:bg-blue-500/5 active:scale-95",
            disabled && "opacity-50 cursor-not-allowed pointer-events-none"
          )}
        >
          <div
            className={cn(
              "w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center transition-colors"
            )}
          >
            {hasUploads ? (
              <Plus className="w-4 h-4 text-blue-400" />
            ) : (
              <Camera className="w-4 h-4 text-blue-400" />
            )}
          </div>
          <span className="text-[10px] font-medium text-zinc-400">
            {hasUploads ? "New" : "Upload"}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileChange}
            disabled={disabled}
          />
        </label>

        {/* Recent cars */}
        {uploads.map((upload) => {
          const isSelected = selectedUploadId === upload.id;

          return (
            <button
              key={upload.id}
              data-upload-id={upload.id}
              onClick={() => {
                if (upload.imageUrl && !disabled) {
                  onSelect(upload.id, upload.imageUrl);
                }
              }}
              disabled={disabled || !upload.imageUrl}
              className={cn(
                "group shrink-0 w-28 h-20 rounded-xl overflow-hidden transition-all duration-300 snap-start",
                "relative",
                !disabled && "hover:scale-[1.02] active:scale-[0.98]",
                disabled && "opacity-50 cursor-not-allowed",
                isSelected
                  ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-zinc-950 shadow-lg shadow-blue-500/20"
                  : "ring-1 ring-white/10 hover:ring-white/20"
              )}
            >
              {/* Car image */}
              {upload.imageUrl ? (
                <Image
                  src={upload.imageUrl}
                  alt="Car"
                  fill
                  sizes="112px"
                  className={cn(
                    "object-cover transition-transform duration-500",
                    !disabled && "group-hover:scale-110"
                  )}
                />
              ) : (
                <div className="w-full h-full bg-zinc-800/50 flex items-center justify-center">
                  <Camera className="w-4 h-4 text-zinc-600" />
                </div>
              )}

              {/* Gradient overlay at bottom */}
              <div className="absolute inset-x-0 bottom-0 h-8 bg-linear-to-t from-black/80 to-transparent" />

              {/* Date label */}
              <div className="absolute bottom-1 left-1.5 right-1.5">
                <span className="text-[9px] font-medium text-white/90 truncate block">
                  {formatDate(new Date(upload.createdAt))}
                </span>
              </div>

              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-0.5 shadow-lg animate-in zoom-in duration-200">
                  <Check className="w-2.5 h-2.5" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Helper text */}
      {hasUploads && (
        <p className="text-[10px] text-zinc-500 mt-2">
          Choose a car to continue • Swipe for more
        </p>
      )}
    </div>
  );
}
